import { Registry } from '../../platform/registry/common/platform.js';
import { localizeWithPath } from '../../nls.js';
import { Extensions as Extensions$1 } from '../../platform/configuration/common/configurationRegistry.js';
import { isMacintosh, isWeb, isWindows, isLinux } from '../../base/common/platform.js';
import { ConfigurationMigrationWorkbenchContribution, DynamicWorkbenchSecurityConfiguration, workbenchConfigurationNodeBase, problemsConfigurationNodeBase, Extensions } from '../common/configuration.js';
import { isStandalone } from '../../base/browser/browser.js';
import { registerWorkbenchContribution2 } from '../common/contributions.js';
import { defaultWindowTitle, defaultWindowTitleSeparator } from './parts/titlebar/windowTitle.js';
import { CustomEditorLabelService } from '../services/editor/common/customEditorLabelService.js';
const _moduleId = "vs/workbench/browser/workbench.contribution";
const registry = ( (Registry.as(Extensions$1.Configuration)));
(function registerConfiguration() {
    registerWorkbenchContribution2(ConfigurationMigrationWorkbenchContribution.ID, ConfigurationMigrationWorkbenchContribution, 4 );
    registerWorkbenchContribution2(DynamicWorkbenchSecurityConfiguration.ID, DynamicWorkbenchSecurityConfiguration, 3 );
    registry.registerConfiguration({
        ...workbenchConfigurationNodeBase,
        'properties': {
            'workbench.editor.titleScrollbarSizing': {
                type: 'string',
                enum: ['default', 'large'],
                enumDescriptions: [
                    ( localizeWithPath(_moduleId, 0, "The default size.")),
                    ( localizeWithPath(
                        _moduleId,
                        1,
                        "Increases the size, so it can be grabbed more easily with the mouse."
                    ))
                ],
                description: ( localizeWithPath(
                    _moduleId,
                    2,
                    "Controls the height of the scrollbars used for tabs and breadcrumbs in the editor title area."
                )),
                default: 'default',
            },
            ["workbench.editor.showTabs" ]: {
                'type': 'string',
                'enum': ["multiple" , "single" , "none" ],
                'enumDescriptions': [
                    ( localizeWithPath(
                        _moduleId,
                        3,
                        "Each editor is displayed as a tab in the editor title area."
                    )),
                    ( localizeWithPath(
                        _moduleId,
                        4,
                        "The active editor is displayed as a single large tab in the editor title area."
                    )),
                    ( localizeWithPath(_moduleId, 5, "The editor title area is not displayed.")),
                ],
                'description': ( localizeWithPath(
                    _moduleId,
                    6,
                    "Controls whether opened editors should show as individual tabs, one single large tab or if the title area should not be shown."
                )),
                'default': 'multiple'
            },
            ["workbench.editor.editorActionsLocation" ]: {
                'type': 'string',
                'enum': ["default" , "titleBar" , "hidden" ],
                'markdownEnumDescriptions': [
                    ( localizeWithPath(
                        _moduleId,
                        7,
                        "Show editor actions in the window title bar when {0} is set to {1}. Otherwise, editor actions are shown in the editor tab bar.",
                        '`#workbench.editor.showTabs#`',
                        '`none`'
                    )),
                    ( localizeWithPath(
                        _moduleId,
                        8,
                        "Show editor actions in the window title bar. If {0} is set to {1}, editor actions are hidden.",
                        '`#window.customTitleBarVisibility#`',
                        '`never`'
                    )),
                    ( localizeWithPath(_moduleId, 9, "Editor actions are not shown.")),
                ],
                'markdownDescription': ( localizeWithPath(_moduleId, 10, "Controls where the editor actions are shown.")),
                'default': 'default'
            },
            'workbench.editor.wrapTabs': {
                'type': 'boolean',
                'markdownDescription': ( localizeWithPath(
                    _moduleId,
                    11,
                    "Controls whether tabs should be wrapped over multiple lines when exceeding available space or whether a scrollbar should appear instead. This value is ignored when {0} is not set to '{1}'.",
                    '`#workbench.editor.showTabs#`',
                    '`multiple`'
                )),
                'default': false
            },
            'workbench.editor.scrollToSwitchTabs': {
                'type': 'boolean',
                'markdownDescription': ( localizeWithPath(
                    _moduleId,
                    12,
                    "Controls whether scrolling over tabs will open them or not. By default tabs will only reveal upon scrolling, but not open. You can press and hold the Shift-key while scrolling to change this behavior for that duration. This value is ignored when {0} is not set to {1}.",
                    '`#workbench.editor.showTabs#`',
                    '`multiple`'
                )),
                'default': false
            },
            'workbench.editor.highlightModifiedTabs': {
                'type': 'boolean',
                'markdownDescription': ( localizeWithPath(
                    _moduleId,
                    13,
                    "Controls whether a top border is drawn on tabs for editors that have unsaved changes. This value is ignored when {0} is not set to {1}.",
                    '`#workbench.editor.showTabs#`',
                    `multiple`
                )),
                'default': false
            },
            'workbench.editor.decorations.badges': {
                'type': 'boolean',
                'markdownDescription': ( localizeWithPath(
                    _moduleId,
                    14,
                    "Controls whether editor file decorations should use badges."
                )),
                'default': true
            },
            'workbench.editor.decorations.colors': {
                'type': 'boolean',
                'markdownDescription': ( localizeWithPath(
                    _moduleId,
                    15,
                    "Controls whether editor file decorations should use colors."
                )),
                'default': true
            },
            [CustomEditorLabelService.SETTING_ID_ENABLED]: {
                'type': 'boolean',
                'markdownDescription': ( localizeWithPath(
                    _moduleId,
                    16,
                    "Controls whether the custom workbench editor labels should be applied."
                )),
                'default': true,
            },
            [CustomEditorLabelService.SETTING_ID_PATTERNS]: {
                'type': 'object',
                'markdownDescription': (() => {
                    let customEditorLabelDescription = ( localizeWithPath(
                        _moduleId,
                        17,
                        "Controls the rendering of the editor label. Each __Item__ is a pattern that matches a file path. Both relative and absolute file paths are supported. In case multiple patterns match, the longest matching path will be picked. Each __Value__ is the template for the rendered editor when the __Item__ matches. Variables are substituted based on the context:"
                    ));
                    customEditorLabelDescription += '\n- ' + [
                        ( localizeWithPath(
                            _moduleId,
                            18,
                            "`${dirname}`: name of the folder in which the file is located (e.g. `root/folder/file.txt -> folder`)."
                        )),
                        ( localizeWithPath(
                            _moduleId,
                            19,
                            "`${dirname(N)}`: name of the nth parent folder in which the file is located (e.g. `N=1: root/folder/file.txt -> root`). Folders can be picked from the start of the path by using negative numbers (e.g. `N=-1: root/folder/file.txt -> root`). If the __Item__ is an absolute pattern path, the first folder (`N=-1`) refers to the first folder in the absoulte path, otherwise it corresponds to the workspace folder."
                        )),
                        ( localizeWithPath(
                            _moduleId,
                            20,
                            "`${filename}`: name of the file without the file extension (e.g. `root/folder/file.txt -> file`)."
                        )),
                        ( localizeWithPath(
                            _moduleId,
                            21,
                            "`${extname}`: the file extension (e.g. `root/folder/file.txt -> txt`)."
                        )),
                    ].join('\n- ');
                    customEditorLabelDescription += '\n\n' + ( localizeWithPath(
                        _moduleId,
                        22,
                        "Example: `\"**/static/**/*.html\": \"${filename} - ${dirname} (${extname})\"` will render a file `root/static/folder/file.html` as `file - folder (html)`."
                    ));
                    return customEditorLabelDescription;
                })(),
                additionalProperties: {
                    type: 'string',
                    markdownDescription: ( localizeWithPath(
                        _moduleId,
                        23,
                        "The template which should be rendered when the pattern mtches. May include the variables ${dirname}, ${filename} and ${extname}."
                    )),
                    minLength: 1,
                    pattern: '.*[a-zA-Z0-9].*'
                },
                'default': {}
            },
            'workbench.editor.labelFormat': {
                'type': 'string',
                'enum': ['default', 'short', 'medium', 'long'],
                'enumDescriptions': [
                    ( localizeWithPath(
                        _moduleId,
                        24,
                        "Show the name of the file. When tabs are enabled and two files have the same name in one group the distinguishing sections of each file's path are added. When tabs are disabled, the path relative to the workspace folder is shown if the editor is active."
                    )),
                    ( localizeWithPath(_moduleId, 25, "Show the name of the file followed by its directory name.")),
                    ( localizeWithPath(
                        _moduleId,
                        26,
                        "Show the name of the file followed by its path relative to the workspace folder."
                    )),
                    ( localizeWithPath(_moduleId, 27, "Show the name of the file followed by its absolute path."))
                ],
                'default': 'default',
                'description': ( localizeWithPath(_moduleId, 28, "Controls the format of the label for an editor.")),
            },
            'workbench.editor.untitled.labelFormat': {
                'type': 'string',
                'enum': ['content', 'name'],
                'enumDescriptions': [
                    ( localizeWithPath(
                        _moduleId,
                        29,
                        "The name of the untitled file is derived from the contents of its first line unless it has an associated file path. It will fallback to the name in case the line is empty or contains no word characters."
                    )),
                    ( localizeWithPath(
                        _moduleId,
                        30,
                        "The name of the untitled file is not derived from the contents of the file."
                    )),
                ],
                'default': 'content',
                'description': ( localizeWithPath(_moduleId, 31, "Controls the format of the label for an untitled editor.")),
            },
            'workbench.editor.empty.hint': {
                'type': 'string',
                'enum': ['text', 'hidden'],
                'default': 'text',
                'markdownDescription': ( localizeWithPath(
                    _moduleId,
                    32,
                    "Controls if the empty editor text hint should be visible in the editor."
                ))
            },
            'workbench.editor.languageDetection': {
                type: 'boolean',
                default: true,
                description: ( localizeWithPath(
                    _moduleId,
                    33,
                    "Controls whether the language in a text editor is automatically detected unless the language has been explicitly set by the language picker. This can also be scoped by language so you can specify which languages you do not want to be switched off of. This is useful for languages like Markdown that often contain other languages that might trick language detection into thinking it's the embedded language and not Markdown."
                )),
                scope: 5
            },
            'workbench.editor.historyBasedLanguageDetection': {
                type: 'boolean',
                default: true,
                tags: ['experimental'],
                description: ( localizeWithPath(
                    _moduleId,
                    34,
                    "Enables use of editor history in language detection. This causes automatic language detection to favor languages that have been recently opened and allows for automatic language detection to operate with smaller inputs."
                )),
            },
            'workbench.editor.preferHistoryBasedLanguageDetection': {
                type: 'boolean',
                default: false,
                tags: ['experimental'],
                description: ( localizeWithPath(
                    _moduleId,
                    35,
                    "When enabled, a language detection model that takes into account editor history will be given higher precedence."
                )),
            },
            'workbench.editor.languageDetectionHints': {
                type: 'object',
                default: { 'untitledEditors': true, 'notebookEditors': true },
                tags: ['experimental'],
                description: ( localizeWithPath(
                    _moduleId,
                    36,
                    "When enabled, shows a Status bar Quick Fix when the editor language doesn't match detected content language."
                )),
                additionalProperties: false,
                properties: {
                    untitledEditors: {
                        type: 'boolean',
                        description: ( localizeWithPath(_moduleId, 37, "Show in untitled text editors")),
                    },
                    notebookEditors: {
                        type: 'boolean',
                        description: ( localizeWithPath(_moduleId, 38, "Show in notebook editors")),
                    }
                }
            },
            'workbench.editor.tabActionLocation': {
                type: 'string',
                enum: ['left', 'right'],
                default: 'right',
                markdownDescription: ( localizeWithPath(
                    _moduleId,
                    39,
                    "Controls the position of the editor's tabs action buttons (close, unpin). This value is ignored when {0} is not set to {1}.",
                    '`#workbench.editor.showTabs#`',
                    '`multiple`'
                ))
            },
            'workbench.editor.tabActionCloseVisibility': {
                type: 'boolean',
                default: true,
                description: ( localizeWithPath(_moduleId, 40, "Controls the visibility of the tab close action button."))
            },
            'workbench.editor.tabActionUnpinVisibility': {
                type: 'boolean',
                default: true,
                description: ( localizeWithPath(_moduleId, 41, "Controls the visibility of the tab unpin action button."))
            },
            'workbench.editor.tabSizing': {
                'type': 'string',
                'enum': ['fit', 'shrink', 'fixed'],
                'default': 'fit',
                'enumDescriptions': [
                    ( localizeWithPath(
                        _moduleId,
                        42,
                        "Always keep tabs large enough to show the full editor label."
                    )),
                    ( localizeWithPath(
                        _moduleId,
                        43,
                        "Allow tabs to get smaller when the available space is not enough to show all tabs at once."
                    )),
                    ( localizeWithPath(
                        _moduleId,
                        44,
                        "Make all tabs the same size, while allowing them to get smaller when the available space is not enough to show all tabs at once."
                    ))
                ],
                'markdownDescription': ( localizeWithPath(
                    _moduleId,
                    45,
                    "Controls the size of editor tabs. This value is ignored when {0} is not set to {1}.",
                    '`#workbench.editor.showTabs#`',
                    '`multiple`'
                ))
            },
            'workbench.editor.tabSizingFixedMinWidth': {
                'type': 'number',
                'default': 50,
                'minimum': 38,
                'markdownDescription': ( localizeWithPath(
                    _moduleId,
                    46,
                    "Controls the minimum width of tabs when {0} size is set to {1}.",
                    '`#workbench.editor.tabSizing#`',
                    '`fixed`'
                ))
            },
            'workbench.editor.tabSizingFixedMaxWidth': {
                'type': 'number',
                'default': 160,
                'minimum': 38,
                'markdownDescription': ( localizeWithPath(
                    _moduleId,
                    47,
                    "Controls the maximum width of tabs when {0} size is set to {1}.",
                    '`#workbench.editor.tabSizing#`',
                    '`fixed`'
                ))
            },
            'window.density.editorTabHeight': {
                'type': 'string',
                'enum': ['default', 'compact'],
                'default': 'default',
                'markdownDescription': ( localizeWithPath(
                    _moduleId,
                    48,
                    "Controls the height of editor tabs. Also applies to the title control bar when {0} is not set to {1}.",
                    '`#workbench.editor.showTabs#`',
                    '`multiple`'
                ))
            },
            'workbench.editor.pinnedTabSizing': {
                'type': 'string',
                'enum': ['normal', 'compact', 'shrink'],
                'default': 'normal',
                'enumDescriptions': [
                    ( localizeWithPath(_moduleId, 49, "A pinned tab inherits the look of non pinned tabs.")),
                    ( localizeWithPath(
                        _moduleId,
                        50,
                        "A pinned tab will show in a compact form with only icon or first letter of the editor name."
                    )),
                    ( localizeWithPath(
                        _moduleId,
                        51,
                        "A pinned tab shrinks to a compact fixed size showing parts of the editor name."
                    ))
                ],
                'markdownDescription': ( localizeWithPath(
                    _moduleId,
                    52,
                    "Controls the size of pinned editor tabs. Pinned tabs are sorted to the beginning of all opened tabs and typically do not close until unpinned. This value is ignored when {0} is not set to {1}.",
                    '`#workbench.editor.showTabs#`',
                    '`multiple`'
                ))
            },
            'workbench.editor.pinnedTabsOnSeparateRow': {
                'type': 'boolean',
                'default': false,
                'markdownDescription': ( localizeWithPath(
                    _moduleId,
                    53,
                    "When enabled, displays pinned tabs in a separate row above all other tabs. This value is ignored when {0} is not set to {1}.",
                    '`#workbench.editor.showTabs#`',
                    '`multiple`'
                )),
            },
            'workbench.editor.preventPinnedEditorClose': {
                'type': 'string',
                'enum': ['keyboardAndMouse', 'keyboard', 'mouse', 'never'],
                'default': 'keyboardAndMouse',
                'enumDescriptions': [
                    ( localizeWithPath(
                        _moduleId,
                        54,
                        "Always prevent closing the pinned editor when using mouse middle click or keyboard."
                    )),
                    ( localizeWithPath(
                        _moduleId,
                        55,
                        "Prevent closing the pinned editor when using the keyboard."
                    )),
                    ( localizeWithPath(
                        _moduleId,
                        56,
                        "Prevent closing the pinned editor when using mouse middle click."
                    )),
                    ( localizeWithPath(_moduleId, 57, "Never prevent closing a pinned editor."))
                ],
                description: ( localizeWithPath(
                    _moduleId,
                    58,
                    "Controls whether pinned editors should close when keyboard or middle mouse click is used for closing."
                )),
            },
            'workbench.editor.splitSizing': {
                'type': 'string',
                'enum': ['auto', 'distribute', 'split'],
                'default': 'auto',
                'enumDescriptions': [
                    ( localizeWithPath(
                        _moduleId,
                        59,
                        "Splits the active editor group to equal parts, unless all editor groups are already in equal parts. In that case, splits all the editor groups to equal parts."
                    )),
                    ( localizeWithPath(_moduleId, 60, "Splits all the editor groups to equal parts.")),
                    ( localizeWithPath(_moduleId, 61, "Splits the active editor group to equal parts."))
                ],
                'description': ( localizeWithPath(_moduleId, 62, "Controls the size of editor groups when splitting them."))
            },
            'workbench.editor.splitOnDragAndDrop': {
                'type': 'boolean',
                'default': true,
                'description': ( localizeWithPath(
                    _moduleId,
                    63,
                    "Controls if editor groups can be split from drag and drop operations by dropping an editor or file on the edges of the editor area."
                ))
            },
            'workbench.editor.dragToOpenWindow': {
                'type': 'boolean',
                'default': true,
                'markdownDescription': ( localizeWithPath(
                    _moduleId,
                    64,
                    "Controls if editors can be dragged out of the window to open them in a new window. Press and hold the `Alt` key while dragging to toggle this dynamically."
                ))
            },
            'workbench.editor.focusRecentEditorAfterClose': {
                'type': 'boolean',
                'description': ( localizeWithPath(
                    _moduleId,
                    65,
                    "Controls whether editors are closed in most recently used order or from left to right."
                )),
                'default': true
            },
            'workbench.editor.showIcons': {
                'type': 'boolean',
                'description': ( localizeWithPath(
                    _moduleId,
                    66,
                    "Controls whether opened editors should show with an icon or not. This requires a file icon theme to be enabled as well."
                )),
                'default': true
            },
            'workbench.editor.enablePreview': {
                'type': 'boolean',
                'description': ( localizeWithPath(
                    _moduleId,
                    67,
                    "Controls whether opened editors show as preview editors. Preview editors do not stay open, are reused until explicitly set to be kept open (via double-click or editing), and show file names in italics."
                )),
                'default': true
            },
            'workbench.editor.enablePreviewFromQuickOpen': {
                'type': 'boolean',
                'markdownDescription': ( localizeWithPath(
                    _moduleId,
                    68,
                    "Controls whether editors opened from Quick Open show as preview editors. Preview editors do not stay open, and are reused until explicitly set to be kept open (via double-click or editing). When enabled, hold Ctrl before selection to open an editor as a non-preview. This value is ignored when {0} is not set to {1}.",
                    '`#workbench.editor.showTabs#`',
                    '`multiple`'
                )),
                'default': false
            },
            'workbench.editor.enablePreviewFromCodeNavigation': {
                'type': 'boolean',
                'markdownDescription': ( localizeWithPath(
                    _moduleId,
                    69,
                    "Controls whether editors remain in preview when a code navigation is started from them. Preview editors do not stay open, and are reused until explicitly set to be kept open (via double-click or editing). This value is ignored when {0} is not set to {1}.",
                    '`#workbench.editor.showTabs#`',
                    '`multiple`'
                )),
                'default': false
            },
            'workbench.editor.closeOnFileDelete': {
                'type': 'boolean',
                'description': ( localizeWithPath(
                    _moduleId,
                    70,
                    "Controls whether editors showing a file that was opened during the session should close automatically when getting deleted or renamed by some other process. Disabling this will keep the editor open  on such an event. Note that deleting from within the application will always close the editor and that editors with unsaved changes will never close to preserve your data."
                )),
                'default': false
            },
            'workbench.editor.openPositioning': {
                'type': 'string',
                'enum': ['left', 'right', 'first', 'last'],
                'default': 'right',
                'markdownDescription': ( localizeWithPath(
                    _moduleId,
                    71,
                    "Controls where editors open. Select {0} or {1} to open editors to the left or right of the currently active one. Select {2} or {3} to open editors independently from the currently active one.",
                    '`left`',
                    '`right`',
                    '`first`',
                    '`last`'
                ))
            },
            'workbench.editor.openSideBySideDirection': {
                'type': 'string',
                'enum': ['right', 'down'],
                'default': 'right',
                'markdownDescription': ( localizeWithPath(
                    _moduleId,
                    72,
                    "Controls the default direction of editors that are opened side by side (for example, from the Explorer). By default, editors will open on the right hand side of the currently active one. If changed to `down`, the editors will open below the currently active one."
                ))
            },
            'workbench.editor.closeEmptyGroups': {
                'type': 'boolean',
                'description': ( localizeWithPath(
                    _moduleId,
                    73,
                    "Controls the behavior of empty editor groups when the last tab in the group is closed. When enabled, empty groups will automatically close. When disabled, empty groups will remain part of the grid."
                )),
                'default': true
            },
            'workbench.editor.revealIfOpen': {
                'type': 'boolean',
                'description': ( localizeWithPath(
                    _moduleId,
                    74,
                    "Controls whether an editor is revealed in any of the visible groups if opened. If disabled, an editor will prefer to open in the currently active editor group. If enabled, an already opened editor will be revealed instead of opened again in the currently active editor group. Note that there are some cases where this setting is ignored, such as when forcing an editor to open in a specific group or to the side of the currently active group."
                )),
                'default': false
            },
            'workbench.editor.mouseBackForwardToNavigate': {
                'type': 'boolean',
                'description': ( localizeWithPath(
                    _moduleId,
                    75,
                    "Enables the use of mouse buttons four and five for commands 'Go Back' and 'Go Forward'."
                )),
                'default': true
            },
            'workbench.editor.navigationScope': {
                'type': 'string',
                'enum': ['default', 'editorGroup', 'editor'],
                'default': 'default',
                'markdownDescription': ( localizeWithPath(
                    _moduleId,
                    76,
                    "Controls the scope of history navigation in editors for commands such as 'Go Back' and 'Go Forward'."
                )),
                'enumDescriptions': [
                    ( localizeWithPath(_moduleId, 77, "Navigate across all opened editors and editor groups.")),
                    ( localizeWithPath(_moduleId, 78, "Navigate only in editors of the active editor group.")),
                    ( localizeWithPath(_moduleId, 79, "Navigate only in the active editor."))
                ],
            },
            'workbench.editor.restoreViewState': {
                'type': 'boolean',
                'markdownDescription': ( localizeWithPath(
                    _moduleId,
                    80,
                    "Restores the last editor view state (such as scroll position) when re-opening editors after they have been closed. Editor view state is stored per editor group and discarded when a group closes. Use the {0} setting to use the last known view state across all editor groups in case no previous view state was found for a editor group.",
                    '`#workbench.editor.sharedViewState#`'
                )),
                'default': true,
                'scope': 5
            },
            'workbench.editor.sharedViewState': {
                'type': 'boolean',
                'description': ( localizeWithPath(
                    _moduleId,
                    81,
                    "Preserves the most recent editor view state (such as scroll position) across all editor groups and restores that if no specific editor view state is found for the editor group."
                )),
                'default': false
            },
            'workbench.editor.splitInGroupLayout': {
                'type': 'string',
                'enum': ['vertical', 'horizontal'],
                'default': 'horizontal',
                'markdownDescription': ( localizeWithPath(
                    _moduleId,
                    82,
                    "Controls the layout for when an editor is split in an editor group to be either vertical or horizontal."
                )),
                'enumDescriptions': [
                    ( localizeWithPath(_moduleId, 83, "Editors are positioned from top to bottom.")),
                    ( localizeWithPath(_moduleId, 84, "Editors are positioned from left to right."))
                ]
            },
            'workbench.editor.centeredLayoutAutoResize': {
                'type': 'boolean',
                'default': true,
                'description': ( localizeWithPath(
                    _moduleId,
                    85,
                    "Controls if the centered layout should automatically resize to maximum width when more than one group is open. Once only one group is open it will resize back to the original centered width."
                ))
            },
            'workbench.editor.centeredLayoutFixedWidth': {
                'type': 'boolean',
                'default': false,
                'description': ( localizeWithPath(
                    _moduleId,
                    86,
                    "Controls whether the centered layout tries to maintain constant width when the window is resized."
                ))
            },
            'workbench.editor.doubleClickTabToToggleEditorGroupSizes': {
                'type': 'string',
                'enum': ['maximize', 'expand', 'off'],
                'default': 'expand',
                'markdownDescription': ( localizeWithPath(
                    _moduleId,
                    87,
                    "Controls how the editor group is resized when double clicking on a tab. This value is ignored when {0} is not set to {1}.",
                    '`#workbench.editor.showTabs#`',
                    '`multiple`'
                )),
                'enumDescriptions': [
                    ( localizeWithPath(
                        _moduleId,
                        88,
                        "All other editor groups are hidden and the current editor group is maximized to take up the entire editor area."
                    )),
                    ( localizeWithPath(
                        _moduleId,
                        89,
                        "The editor group takes as much space as possible by making all other editor groups as small as possible."
                    )),
                    ( localizeWithPath(_moduleId, 90, "No editor group is resized when double clicking on a tab."))
                ]
            },
            'workbench.editor.limit.enabled': {
                'type': 'boolean',
                'default': false,
                'description': ( localizeWithPath(
                    _moduleId,
                    91,
                    "Controls if the number of opened editors should be limited or not. When enabled, less recently used editors will close to make space for newly opening editors."
                ))
            },
            'workbench.editor.limit.value': {
                'type': 'number',
                'default': 10,
                'exclusiveMinimum': 0,
                'markdownDescription': ( localizeWithPath(
                    _moduleId,
                    92,
                    "Controls the maximum number of opened editors. Use the {0} setting to control this limit per editor group or across all groups.",
                    '`#workbench.editor.limit.perEditorGroup#`'
                ))
            },
            'workbench.editor.limit.excludeDirty': {
                'type': 'boolean',
                'default': false,
                'description': ( localizeWithPath(
                    _moduleId,
                    93,
                    "Controls if the maximum number of opened editors should exclude dirty editors for counting towards the configured limit."
                ))
            },
            'workbench.editor.limit.perEditorGroup': {
                'type': 'boolean',
                'default': false,
                'description': ( localizeWithPath(
                    _moduleId,
                    94,
                    "Controls if the limit of maximum opened editors should apply per editor group or across all editor groups."
                ))
            },
            'workbench.localHistory.enabled': {
                'type': 'boolean',
                'default': true,
                'description': ( localizeWithPath(
                    _moduleId,
                    95,
                    "Controls whether local file history is enabled. When enabled, the file contents of an editor that is saved will be stored to a backup location to be able to restore or review the contents later. Changing this setting has no effect on existing local file history entries."
                )),
                'scope': 4
            },
            'workbench.localHistory.maxFileSize': {
                'type': 'number',
                'default': 256,
                'minimum': 1,
                'description': ( localizeWithPath(
                    _moduleId,
                    96,
                    "Controls the maximum size of a file (in KB) to be considered for local file history. Files that are larger will not be added to the local file history. Changing this setting has no effect on existing local file history entries."
                )),
                'scope': 4
            },
            'workbench.localHistory.maxFileEntries': {
                'type': 'number',
                'default': 50,
                'minimum': 0,
                'description': ( localizeWithPath(
                    _moduleId,
                    97,
                    "Controls the maximum number of local file history entries per file. When the number of local file history entries exceeds this number for a file, the oldest entries will be discarded."
                )),
                'scope': 4
            },
            'workbench.localHistory.exclude': {
                'type': 'object',
                'patternProperties': {
                    '.*': { 'type': 'boolean' }
                },
                'markdownDescription': ( localizeWithPath(
                    _moduleId,
                    98,
                    "Configure paths or [glob patterns](https://aka.ms/vscode-glob-patterns) for excluding files from the local file history. Glob patterns are always evaluated relative to the path of the workspace folder unless they are absolute paths. Changing this setting has no effect on existing local file history entries."
                )),
                'scope': 4
            },
            'workbench.localHistory.mergeWindow': {
                'type': 'number',
                'default': 10,
                'minimum': 1,
                'markdownDescription': ( localizeWithPath(
                    _moduleId,
                    99,
                    "Configure an interval in seconds during which the last entry in local file history is replaced with the entry that is being added. This helps reduce the overall number of entries that are added, for example when auto save is enabled. This setting is only applied to entries that have the same source of origin. Changing this setting has no effect on existing local file history entries."
                )),
                'scope': 4
            },
            'workbench.commandPalette.history': {
                'type': 'number',
                'description': ( localizeWithPath(
                    _moduleId,
                    100,
                    "Controls the number of recently used commands to keep in history for the command palette. Set to 0 to disable command history."
                )),
                'default': 50,
                'minimum': 0
            },
            'workbench.commandPalette.preserveInput': {
                'type': 'boolean',
                'description': ( localizeWithPath(
                    _moduleId,
                    101,
                    "Controls whether the last typed input to the command palette should be restored when opening it the next time."
                )),
                'default': false
            },
            'workbench.commandPalette.experimental.suggestCommands': {
                'type': 'boolean',
                tags: ['experimental'],
                'description': ( localizeWithPath(
                    _moduleId,
                    102,
                    "Controls whether the command palette should have a list of commonly used commands."
                )),
                'default': false
            },
            'workbench.commandPalette.experimental.askChatLocation': {
                'type': 'string',
                tags: ['experimental'],
                'description': ( localizeWithPath(
                    _moduleId,
                    103,
                    "Controls where the command palette should ask chat questions."
                )),
                'default': 'chatView',
                enum: ['chatView', 'quickChat'],
                enumDescriptions: [
                    ( localizeWithPath(_moduleId, 104, "Ask chat questions in the Chat view.")),
                    ( localizeWithPath(_moduleId, 105, "Ask chat questions in Quick Chat."))
                ]
            },
            'workbench.commandPalette.experimental.enableNaturalLanguageSearch': {
                'type': 'boolean',
                tags: ['experimental'],
                'description': ( localizeWithPath(
                    _moduleId,
                    106,
                    "Controls whether the command palette should include similar commands. You must have an extension installed that provides Natural Language support."
                )),
                'default': true
            },
            'workbench.quickOpen.closeOnFocusLost': {
                'type': 'boolean',
                'description': ( localizeWithPath(
                    _moduleId,
                    107,
                    "Controls whether Quick Open should close automatically once it loses focus."
                )),
                'default': true
            },
            'workbench.quickOpen.preserveInput': {
                'type': 'boolean',
                'description': ( localizeWithPath(
                    _moduleId,
                    108,
                    "Controls whether the last typed input to Quick Open should be restored when opening it the next time."
                )),
                'default': false
            },
            'workbench.settings.openDefaultSettings': {
                'type': 'boolean',
                'description': ( localizeWithPath(
                    _moduleId,
                    109,
                    "Controls whether opening settings also opens an editor showing all default settings."
                )),
                'default': false
            },
            'workbench.settings.useSplitJSON': {
                'type': 'boolean',
                'markdownDescription': ( localizeWithPath(
                    _moduleId,
                    110,
                    "Controls whether to use the split JSON editor when editing settings as JSON."
                )),
                'default': false
            },
            'workbench.settings.openDefaultKeybindings': {
                'type': 'boolean',
                'description': ( localizeWithPath(
                    _moduleId,
                    111,
                    "Controls whether opening keybinding settings also opens an editor showing all default keybindings."
                )),
                'default': false
            },
            'workbench.sideBar.location': {
                'type': 'string',
                'enum': ['left', 'right'],
                'default': 'left',
                'description': ( localizeWithPath(
                    _moduleId,
                    112,
                    "Controls the location of the primary side bar and activity bar. They can either show on the left or right of the workbench. The secondary side bar will show on the opposite side of the workbench."
                ))
            },
            'workbench.panel.defaultLocation': {
                'type': 'string',
                'enum': ['left', 'bottom', 'right'],
                'default': 'bottom',
                'description': ( localizeWithPath(
                    _moduleId,
                    113,
                    "Controls the default location of the panel (Terminal, Debug Console, Output, Problems) in a new workspace. It can either show at the bottom, right, or left of the editor area."
                )),
            },
            'workbench.panel.opensMaximized': {
                'type': 'string',
                'enum': ['always', 'never', 'preserve'],
                'default': 'preserve',
                'description': ( localizeWithPath(
                    _moduleId,
                    114,
                    "Controls whether the panel opens maximized. It can either always open maximized, never open maximized, or open to the last state it was in before being closed."
                )),
                'enumDescriptions': [
                    ( localizeWithPath(_moduleId, 115, "Always maximize the panel when opening it.")),
                    ( localizeWithPath(
                        _moduleId,
                        116,
                        "Never maximize the panel when opening it. The panel will open un-maximized."
                    )),
                    ( localizeWithPath(
                        _moduleId,
                        117,
                        "Open the panel to the state that it was in, before it was closed."
                    ))
                ]
            },
            'workbench.statusBar.visible': {
                'type': 'boolean',
                'default': true,
                'description': ( localizeWithPath(
                    _moduleId,
                    118,
                    "Controls the visibility of the status bar at the bottom of the workbench."
                ))
            },
            ["workbench.activityBar.location" ]: {
                'type': 'string',
                'enum': ['default', 'top', 'bottom', 'hidden'],
                'default': 'default',
                'markdownDescription': ( localizeWithPath(
                    _moduleId,
                    119,
                    "Controls the location of the Activity Bar relative to the Primary and Secondary Side Bars."
                )),
                'enumDescriptions': [
                    ( localizeWithPath(
                        _moduleId,
                        120,
                        "Show the Activity Bar on the side of the Primary Side Bar and on top of the Secondary Side Bar."
                    )),
                    ( localizeWithPath(
                        _moduleId,
                        121,
                        "Show the Activity Bar on top of the Primary and Secondary Side Bars."
                    )),
                    ( localizeWithPath(
                        _moduleId,
                        122,
                        "Show the Activity Bar at the bottom of the Primary and Secondary Side Bars."
                    )),
                    ( localizeWithPath(
                        _moduleId,
                        123,
                        "Hide the Activity Bar in the Primary and Secondary Side Bars."
                    ))
                ],
            },
            'workbench.activityBar.iconClickBehavior': {
                'type': 'string',
                'enum': ['toggle', 'focus'],
                'default': 'toggle',
                'markdownDescription': ( localizeWithPath(
                    _moduleId,
                    124,
                    "Controls the behavior of clicking an Activity Bar icon in the workbench. This value is ignored when {0} is not set to {1}.",
                    '`#workbench.activityBar.location#`',
                    '`default`'
                )),
                'enumDescriptions': [
                    ( localizeWithPath(
                        _moduleId,
                        125,
                        "Hide the Primary Side Bar if the clicked item is already visible."
                    )),
                    ( localizeWithPath(
                        _moduleId,
                        126,
                        "Focus the Primary Side Bar if the clicked item is already visible."
                    ))
                ]
            },
            'workbench.view.alwaysShowHeaderActions': {
                'type': 'boolean',
                'default': false,
                'description': ( localizeWithPath(
                    _moduleId,
                    127,
                    "Controls the visibility of view header actions. View header actions may either be always visible, or only visible when that view is focused or hovered over."
                ))
            },
            'workbench.fontAliasing': {
                'type': 'string',
                'enum': ['default', 'antialiased', 'none', 'auto'],
                'default': 'default',
                'description': ( localizeWithPath(_moduleId, 128, "Controls font aliasing method in the workbench.")),
                'enumDescriptions': [
                    ( localizeWithPath(
                        _moduleId,
                        129,
                        "Sub-pixel font smoothing. On most non-retina displays this will give the sharpest text."
                    )),
                    ( localizeWithPath(
                        _moduleId,
                        130,
                        "Smooth the font on the level of the pixel, as opposed to the subpixel. Can make the font appear lighter overall."
                    )),
                    ( localizeWithPath(
                        _moduleId,
                        131,
                        "Disables font smoothing. Text will show with jagged sharp edges."
                    )),
                    ( localizeWithPath(
                        _moduleId,
                        132,
                        "Applies `default` or `antialiased` automatically based on the DPI of displays."
                    ))
                ],
                'included': isMacintosh
            },
            'workbench.settings.editor': {
                'type': 'string',
                'enum': ['ui', 'json'],
                'enumDescriptions': [
                    ( localizeWithPath(_moduleId, 133, "Use the settings UI editor.")),
                    ( localizeWithPath(_moduleId, 134, "Use the JSON file editor.")),
                ],
                'description': ( localizeWithPath(_moduleId, 135, "Determines which settings editor to use by default.")),
                'default': 'ui',
                'scope': 3
            },
            'workbench.hover.delay': {
                'type': 'number',
                'description': ( localizeWithPath(
                    _moduleId,
                    136,
                    "Controls the delay in milliseconds after which the hover is shown for workbench items (ex. some extension provided tree view items). Already visible items may require a refresh before reflecting this setting change."
                )),
                'default': isMacintosh ? 1500 : 500,
                'minimum': 0
            },
            'workbench.reduceMotion': {
                type: 'string',
                description: ( localizeWithPath(
                    _moduleId,
                    137,
                    "Controls whether the workbench should render with fewer animations."
                )),
                'enumDescriptions': [
                    ( localizeWithPath(_moduleId, 138, "Always render with reduced motion.")),
                    ( localizeWithPath(_moduleId, 139, "Do not render with reduced motion")),
                    ( localizeWithPath(_moduleId, 140, "Render with reduced motion based on OS configuration.")),
                ],
                default: 'auto',
                tags: ['accessibility'],
                enum: ['on', 'off', 'auto']
            },
            ["workbench.layoutControl.enabled" ]: {
                'type': 'boolean',
                'default': true,
                'markdownDescription': isWeb ?
                    ( localizeWithPath(
                        _moduleId,
                        141,
                        "Controls whether the layout control in the title bar is shown."
                    )) :
                    ( localizeWithPath(
                        _moduleId,
                        142,
                        "Controls whether the layout control is shown in the custom title bar. This setting only has an effect when {0} is not set to {1}.",
                        '`#window.customTitleBarVisibility#`',
                        '`never`'
                    ))
            },
            'workbench.layoutControl.type': {
                'type': 'string',
                'enum': ['menu', 'toggles', 'both'],
                'enumDescriptions': [
                    ( localizeWithPath(_moduleId, 143, "Shows a single button with a dropdown of layout options.")),
                    ( localizeWithPath(
                        _moduleId,
                        144,
                        "Shows several buttons for toggling the visibility of the panels and side bar."
                    )),
                    ( localizeWithPath(_moduleId, 145, "Shows both the dropdown and toggle buttons.")),
                ],
                'default': 'both',
                'description': ( localizeWithPath(
                    _moduleId,
                    146,
                    "Controls whether the layout control in the custom title bar is displayed as a single menu button or with multiple UI toggles."
                )),
            },
            'workbench.tips.enabled': {
                'type': 'boolean',
                'default': true,
                'description': ( localizeWithPath(
                    _moduleId,
                    147,
                    "When enabled, will show the watermark tips when no editor is open."
                ))
            },
        }
    });
    let windowTitleDescription = ( localizeWithPath(
        _moduleId,
        148,
        "Controls the window title based on the current context such as the opened workspace or active editor. Variables are substituted based on the context:"
    ));
    windowTitleDescription += '\n- ' + [
        ( localizeWithPath(_moduleId, 149, "`${activeEditorShort}`: the file name (e.g. myFile.txt).")),
        ( localizeWithPath(
            _moduleId,
            150,
            "`${activeEditorMedium}`: the path of the file relative to the workspace folder (e.g. myFolder/myFileFolder/myFile.txt)."
        )),
        ( localizeWithPath(
            _moduleId,
            151,
            "`${activeEditorLong}`: the full path of the file (e.g. /Users/Development/myFolder/myFileFolder/myFile.txt)."
        )),
        ( localizeWithPath(
            _moduleId,
            152,
            "`${activeFolderShort}`: the name of the folder the file is contained in (e.g. myFileFolder)."
        )),
        ( localizeWithPath(
            _moduleId,
            153,
            "`${activeFolderMedium}`: the path of the folder the file is contained in, relative to the workspace folder (e.g. myFolder/myFileFolder)."
        )),
        ( localizeWithPath(
            _moduleId,
            154,
            "`${activeFolderLong}`: the full path of the folder the file is contained in (e.g. /Users/Development/myFolder/myFileFolder)."
        )),
        ( localizeWithPath(
            _moduleId,
            155,
            "`${folderName}`: name of the workspace folder the file is contained in (e.g. myFolder)."
        )),
        ( localizeWithPath(
            _moduleId,
            156,
            "`${folderPath}`: file path of the workspace folder the file is contained in (e.g. /Users/Development/myFolder)."
        )),
        ( localizeWithPath(
            _moduleId,
            157,
            "`${rootName}`: name of the workspace with optional remote name and workspace indicator if applicable (e.g. myFolder, myRemoteFolder [SSH] or myWorkspace (Workspace))."
        )),
        ( localizeWithPath(
            _moduleId,
            158,
            "`${rootNameShort}`: shortened name of the workspace without suffixes (e.g. myFolder, myRemoteFolder or myWorkspace)."
        )),
        ( localizeWithPath(
            _moduleId,
            159,
            "`${rootPath}`: file path of the opened workspace or folder (e.g. /Users/Development/myWorkspace)."
        )),
        ( localizeWithPath(
            _moduleId,
            160,
            "`${profileName}`: name of the profile in which the workspace is opened (e.g. Data Science (Profile)). Ignored if default profile is used."
        )),
        ( localizeWithPath(_moduleId, 161, "`${appName}`: e.g. VS Code.")),
        ( localizeWithPath(_moduleId, 162, "`${remoteName}`: e.g. SSH")),
        ( localizeWithPath(
            _moduleId,
            163,
            "`${dirty}`: an indicator for when the active editor has unsaved changes."
        )),
        ( localizeWithPath(
            _moduleId,
            164,
            "`${focusedView}`: the name of the view that is currently focused."
        )),
        ( localizeWithPath(
            _moduleId,
            165,
            "`${activeRepositoryName}`: the name of the active repository (e.g. vscode)."
        )),
        ( localizeWithPath(
            _moduleId,
            166,
            "`${activeRepositoryBranchName}`: the name of the active branch in the active repository (e.g. main)."
        )),
        ( localizeWithPath(
            _moduleId,
            167,
            "`${separator}`: a conditional separator (\" - \") that only shows when surrounded by variables with values or static text."
        ))
    ].join('\n- ');
    registry.registerConfiguration({
        'id': 'window',
        'order': 8,
        'title': ( localizeWithPath(_moduleId, 168, "Window")),
        'type': 'object',
        'properties': {
            'window.title': {
                'type': 'string',
                'default': defaultWindowTitle,
                'markdownDescription': windowTitleDescription
            },
            'window.titleSeparator': {
                'type': 'string',
                'default': defaultWindowTitleSeparator,
                'markdownDescription': ( localizeWithPath(_moduleId, 169, "Separator used by {0}.", '`#window.title#`'))
            },
            ["window.commandCenter" ]: {
                type: 'boolean',
                default: true,
                markdownDescription: isWeb ?
                    ( localizeWithPath(_moduleId, 170, "Show command launcher together with the window title.")) :
                    ( localizeWithPath(
                        _moduleId,
                        171,
                        "Show command launcher together with the window title. This setting only has an effect when {0} is not set to {1}.",
                        '`#window.customTitleBarVisibility#`',
                        '`never`'
                    ))
            },
            'window.menuBarVisibility': {
                'type': 'string',
                'enum': ['classic', 'visible', 'toggle', 'hidden', 'compact'],
                'markdownEnumDescriptions': [
                    ( localizeWithPath(
                        _moduleId,
                        172,
                        "Menu is displayed at the top of the window and only hidden in full screen mode."
                    )),
                    ( localizeWithPath(
                        _moduleId,
                        173,
                        "Menu is always visible at the top of the window even in full screen mode."
                    )),
                    isMacintosh ?
                        ( localizeWithPath(
                            _moduleId,
                            174,
                            "Menu is hidden but can be displayed at the top of the window by executing the `Focus Application Menu` command."
                        )) :
                        ( localizeWithPath(
                            _moduleId,
                            175,
                            "Menu is hidden but can be displayed at the top of the window via the Alt key."
                        )),
                    ( localizeWithPath(_moduleId, 176, "Menu is always hidden.")),
                    isWeb ?
                        ( localizeWithPath(_moduleId, 177, "Menu is displayed as a compact button in the side bar.")) :
                        ( localizeWithPath(
                            _moduleId,
                            178,
                            "Menu is displayed as a compact button in the side bar. This value is ignored when {0} is {1}.",
                            '`#window.titleBarStyle#`',
                            '`native`'
                        ))
                ],
                'default': isWeb ? 'compact' : 'classic',
                'scope': 1 ,
                'markdownDescription': isMacintosh ?
                    ( localizeWithPath(
                        _moduleId,
                        179,
                        "Control the visibility of the menu bar. A setting of 'toggle' means that the menu bar is hidden and executing `Focus Application Menu` will show it. A setting of 'compact' will move the menu into the side bar."
                    )) :
                    ( localizeWithPath(
                        _moduleId,
                        180,
                        "Control the visibility of the menu bar. A setting of 'toggle' means that the menu bar is hidden and a single press of the Alt key will show it. A setting of 'compact' will move the menu into the side bar."
                    )),
                'included': isWindows || isLinux || isWeb
            },
            'window.enableMenuBarMnemonics': {
                'type': 'boolean',
                'default': true,
                'scope': 1 ,
                'description': ( localizeWithPath(
                    _moduleId,
                    181,
                    "Controls whether the main menus can be opened via Alt-key shortcuts. Disabling mnemonics allows to bind these Alt-key shortcuts to editor commands instead."
                )),
                'included': isWindows || isLinux
            },
            'window.customMenuBarAltFocus': {
                'type': 'boolean',
                'default': true,
                'scope': 1 ,
                'markdownDescription': ( localizeWithPath(
                    _moduleId,
                    182,
                    "Controls whether the menu bar will be focused by pressing the Alt-key. This setting has no effect on toggling the menu bar with the Alt-key."
                )),
                'included': isWindows || isLinux
            },
            'window.openFilesInNewWindow': {
                'type': 'string',
                'enum': ['on', 'off', 'default'],
                'enumDescriptions': [
                    ( localizeWithPath(_moduleId, 183, "Files will open in a new window.")),
                    ( localizeWithPath(
                        _moduleId,
                        184,
                        "Files will open in the window with the files' folder open or the last active window."
                    )),
                    isMacintosh ?
                        ( localizeWithPath(
                            _moduleId,
                            185,
                            "Files will open in the window with the files' folder open or the last active window unless opened via the Dock or from Finder."
                        )) :
                        ( localizeWithPath(
                            _moduleId,
                            186,
                            "Files will open in a new window unless picked from within the application (e.g. via the File menu)."
                        ))
                ],
                'default': 'off',
                'scope': 1 ,
                'markdownDescription': isMacintosh ?
                    ( localizeWithPath(
                        _moduleId,
                        187,
                        "Controls whether files should open in a new window when using a command line or file dialog.\nNote that there can still be cases where this setting is ignored (e.g. when using the `--new-window` or `--reuse-window` command line option)."
                    )) :
                    ( localizeWithPath(
                        _moduleId,
                        188,
                        "Controls whether files should open in a new window when using a command line or file dialog.\nNote that there can still be cases where this setting is ignored (e.g. when using the `--new-window` or `--reuse-window` command line option)."
                    ))
            },
            'window.openFoldersInNewWindow': {
                'type': 'string',
                'enum': ['on', 'off', 'default'],
                'enumDescriptions': [
                    ( localizeWithPath(_moduleId, 189, "Folders will open in a new window.")),
                    ( localizeWithPath(_moduleId, 190, "Folders will replace the last active window.")),
                    ( localizeWithPath(
                        _moduleId,
                        191,
                        "Folders will open in a new window unless a folder is picked from within the application (e.g. via the File menu)."
                    ))
                ],
                'default': 'default',
                'scope': 1 ,
                'markdownDescription': ( localizeWithPath(
                    _moduleId,
                    192,
                    "Controls whether folders should open in a new window or replace the last active window.\nNote that there can still be cases where this setting is ignored (e.g. when using the `--new-window` or `--reuse-window` command line option)."
                ))
            },
            'window.confirmBeforeClose': {
                'type': 'string',
                'enum': ['always', 'keyboardOnly', 'never'],
                'enumDescriptions': [
                    isWeb ?
                        ( localizeWithPath(
                            _moduleId,
                            193,
                            "Always try to ask for confirmation. Note that browsers may still decide to close a tab or window without confirmation."
                        )) :
                        ( localizeWithPath(_moduleId, 194, "Always ask for confirmation.")),
                    isWeb ?
                        ( localizeWithPath(
                            _moduleId,
                            195,
                            "Only ask for confirmation if a keybinding was used to close the window. Note that detection may not be possible in some cases."
                        )) :
                        ( localizeWithPath(_moduleId, 196, "Only ask for confirmation if a keybinding was used.")),
                    isWeb ?
                        ( localizeWithPath(
                            _moduleId,
                            197,
                            "Never explicitly ask for confirmation unless data loss is imminent."
                        )) :
                        ( localizeWithPath(_moduleId, 198, "Never explicitly ask for confirmation."))
                ],
                'default': (isWeb && !isStandalone()) ? 'keyboardOnly' : 'never',
                'markdownDescription': isWeb ?
                    ( localizeWithPath(
                        _moduleId,
                        199,
                        "Controls whether to show a confirmation dialog before closing the browser tab or window. Note that even if enabled, browsers may still decide to close a tab or window without confirmation and that this setting is only a hint that may not work in all cases."
                    )) :
                    ( localizeWithPath(
                        _moduleId,
                        200,
                        "Controls whether to show a confirmation dialog before closing a window or quitting the application."
                    )),
                'scope': 1
            }
        }
    });
    registry.registerConfiguration({
        ...problemsConfigurationNodeBase,
        'properties': {
            'problems.visibility': {
                'type': 'boolean',
                'default': true,
                'description': ( localizeWithPath(
                    _moduleId,
                    201,
                    "Controls whether the problems are visible throughout the editor and workbench."
                )),
            },
        }
    });
    registry.registerConfiguration({
        'id': 'zenMode',
        'order': 9,
        'title': ( localizeWithPath(_moduleId, 202, "Zen Mode")),
        'type': 'object',
        'properties': {
            'zenMode.fullScreen': {
                'type': 'boolean',
                'default': true,
                'description': ( localizeWithPath(
                    _moduleId,
                    203,
                    "Controls whether turning on Zen Mode also puts the workbench into full screen mode."
                ))
            },
            'zenMode.centerLayout': {
                'type': 'boolean',
                'default': true,
                'description': ( localizeWithPath(
                    _moduleId,
                    204,
                    "Controls whether turning on Zen Mode also centers the layout."
                ))
            },
            'zenMode.showTabs': {
                'type': 'string',
                'enum': ['multiple', 'single', 'none'],
                'description': ( localizeWithPath(
                    _moduleId,
                    205,
                    "Controls whether turning on Zen Mode should show multiple editor tabs, a single editor tab, or hide the editor title area completely."
                )),
                'enumDescriptions': [
                    ( localizeWithPath(
                        _moduleId,
                        206,
                        "Each editor is displayed as a tab in the editor title area."
                    )),
                    ( localizeWithPath(
                        _moduleId,
                        207,
                        "The active editor is displayed as a single large tab in the editor title area."
                    )),
                    ( localizeWithPath(_moduleId, 208, "The editor title area is not displayed.")),
                ],
                'default': 'multiple'
            },
            'zenMode.hideStatusBar': {
                'type': 'boolean',
                'default': true,
                'description': ( localizeWithPath(
                    _moduleId,
                    209,
                    "Controls whether turning on Zen Mode also hides the status bar at the bottom of the workbench."
                ))
            },
            'zenMode.hideActivityBar': {
                'type': 'boolean',
                'default': true,
                'description': ( localizeWithPath(
                    _moduleId,
                    210,
                    "Controls whether turning on Zen Mode also hides the activity bar either at the left or right of the workbench."
                ))
            },
            'zenMode.hideLineNumbers': {
                'type': 'boolean',
                'default': true,
                'description': ( localizeWithPath(
                    _moduleId,
                    211,
                    "Controls whether turning on Zen Mode also hides the editor line numbers."
                ))
            },
            'zenMode.restore': {
                'type': 'boolean',
                'default': true,
                'description': ( localizeWithPath(
                    _moduleId,
                    212,
                    "Controls whether a window should restore to Zen Mode if it was exited in Zen Mode."
                ))
            },
            'zenMode.silentNotifications': {
                'type': 'boolean',
                'default': true,
                'description': ( localizeWithPath(
                    _moduleId,
                    213,
                    "Controls whether notifications do not disturb mode should be enabled while in Zen Mode. If true, only error notifications will pop out."
                ))
            }
        }
    });
})();
( (Registry.as(Extensions.ConfigurationMigration)))
    .registerConfigurationMigrations([{
        key: 'workbench.activityBar.visible', migrateFn: (value) => {
            const result = [];
            if (value !== undefined) {
                result.push(['workbench.activityBar.visible', { value: undefined }]);
            }
            if (value === false) {
                result.push(["workbench.activityBar.location" , { value: "hidden"  }]);
            }
            return result;
        }
    }]);
( (Registry.as(Extensions.ConfigurationMigration)))
    .registerConfigurationMigrations([{
        key: "workbench.activityBar.location" , migrateFn: (value) => {
            const results = [];
            if (value === 'side') {
                results.push(["workbench.activityBar.location" , { value: "default"  }]);
            }
            return results;
        }
    }]);
( (Registry.as(Extensions.ConfigurationMigration)))
    .registerConfigurationMigrations([{
        key: 'workbench.editor.doubleClickTabToToggleEditorGroupSizes', migrateFn: (value) => {
            const results = [];
            if (typeof value === 'boolean') {
                value = value ? 'expand' : 'off';
                results.push(['workbench.editor.doubleClickTabToToggleEditorGroupSizes', { value }]);
            }
            return results;
        }
    }, {
        key: "workbench.editor.showTabs" , migrateFn: (value) => {
            const results = [];
            if (typeof value === 'boolean') {
                value = value ? "multiple"  : "single" ;
                results.push(["workbench.editor.showTabs" , { value }]);
            }
            return results;
        }
    }, {
        key: 'workbench.editor.tabCloseButton', migrateFn: (value) => {
            const result = [];
            if (value === 'left' || value === 'right') {
                result.push(['workbench.editor.tabActionLocation', { value }]);
            }
            else if (value === 'off') {
                result.push(['workbench.editor.tabActionCloseVisibility', { value: false }]);
            }
            return result;
        }
    }, {
        key: 'zenMode.hideTabs', migrateFn: (value) => {
            const result = [['zenMode.hideTabs', { value: undefined }]];
            if (value === true) {
                result.push(['zenMode.showTabs', { value: 'single' }]);
            }
            return result;
        }
    }]);
